import clsx from 'clsx'
import Image from 'next/image'
import { useFormContext } from 'react-hook-form'
import { getFormattedCurrency } from '@ancon/wildcat-utils/currency'
import calculateIngredientsCost from '@ancon/wildcat-utils/product/calculateIngredientsCost'
import calculateAddonGroupsCost from '@ancon/wildcat-utils/product/calculateAddonGroupCost'
import ProductPlaceholderIcon from '@ancon/wildcat-ui/shared/static/placeholders/product-v2.svg'

import BodyText from '../../app/components/BodyText'
import HeadingText from '../../app/components/HeadingText'
import AdvancedPricingTag from '../components/AdvancedPricingTag'
import ProductAllergyInformation from '../components/ProductAllergyInformation'
import useAppSelector from '../../../store/hooks/useAppSelector'
import getImageUrl from '../../app/utils/getImageUrl'
import {
  productDetailsVariantByIdSelector,
  productsDetailsAddonGroupsSelector,
  productsDetailsAllowDynamicPricingSelector,
  productsDetailsDescriptionSelector,
  productsDetailsHasVariantsSelector,
  productsDetailsImageIdSelector,
  productsDetailsIngredientsSelector,
  productsDetailsMaximumIngredientsForDynamicAddonSelector,
  productsDetailsProductNameSelector,
  productsDetailsVariantsSelector,
} from '../store/productSelectors'
import ProductName from '../components/ProductName'
import { ConfigureProductFormState } from '../types'

import styles from './ProductMetaForm.module.scss'

type ProductMetaFormProps = {
  showFloatingHeader?: boolean
}

export default function ProductMetaForm({
  showFloatingHeader,
}: ProductMetaFormProps) {
  const { watch } = useFormContext<ConfigureProductFormState>()
  const [selectedVariantId, selectedIngredients, selectedAddons] = watch([
    'selectedVariantId',
    'selectedIngredients',
    'selectedAddons',
  ])

  const imageId = useAppSelector(productsDetailsImageIdSelector)
  const hasVariants = useAppSelector(productsDetailsHasVariantsSelector)
  const variants = useAppSelector(productsDetailsVariantsSelector)
  const productName = useAppSelector(productsDetailsProductNameSelector)
  const description = useAppSelector(productsDetailsDescriptionSelector)
  const selectedVariant = useAppSelector(state =>
    productDetailsVariantByIdSelector(state, selectedVariantId),
  )
  const addonGroups = useAppSelector(productsDetailsAddonGroupsSelector)
  const ingredients = useAppSelector(productsDetailsIngredientsSelector)
  const allowDynamicPricing = useAppSelector(
    productsDetailsAllowDynamicPricingSelector,
  )
  const maximumDynamicPricingIngredients = useAppSelector(
    productsDetailsMaximumIngredientsForDynamicAddonSelector,
  )
  const ingredientsPrice =
    ingredients && maximumDynamicPricingIngredients
      ? calculateIngredientsCost(
          ingredients,
          selectedIngredients,
          allowDynamicPricing,
          maximumDynamicPricingIngredients,
        )
      : 0
  const addonGroupsPrice = addonGroups
    ? calculateAddonGroupsCost(addonGroups, selectedAddons)
    : 0

  const imageUrl = imageId ? getImageUrl(imageId) : null
  const priceAddition = ingredientsPrice + addonGroupsPrice

  const selectedVariantPrice = selectedVariant?.variantPrice

  function getVariantName() {
    if (hasVariants && !!selectedVariant) {
      return selectedVariant.customerFacingName || selectedVariant.name
    }

    return productName
  }

  function getFormattedPriceAddition() {
    const mainProductVariant = variants?.find(v => v.isMainProductVariant)

    const currency =
      selectedVariantPrice?.unitPriceCurrency ??
      mainProductVariant?.variantPrice.unitPriceCurrency

    return getFormattedCurrency(priceAddition, currency)
  }

  return (
    <div className={styles.container}>
      <div
        className={clsx(styles.floatingHeader, {
          [styles.show]: showFloatingHeader,
        })}
      >
        <HeadingText as="h2">{getVariantName()}</HeadingText>
      </div>

      <div className={styles.imageContainer}>
        {imageUrl && <img src={imageUrl} alt="product" />}
        {!imageUrl && (
          <Image
            alt={productName || 'product placeholder image'}
            className={styles.productPlaceholderImage}
            src={ProductPlaceholderIcon}
          />
        )}
      </div>

      <div className={styles.metaContainer}>
        <ProductName as="h2">{getVariantName()}</ProductName>
        <div className={styles.pricingContainer}>
          {!!selectedVariantPrice && (
            <HeadingText as="h3">
              {getFormattedCurrency(
                selectedVariantPrice.unitPrice,
                selectedVariantPrice.unitPriceCurrency,
              )}
            </HeadingText>
          )}
          {priceAddition > 0 && (
            <BodyText
              color="body-1"
              className={clsx(styles.priceAddition, {
                [styles.hasSelectedVariant]: !!selectedVariantPrice,
              })}
            >
              <BodyText color="body-1" as="span">
                +
              </BodyText>
              {getFormattedPriceAddition()}
            </BodyText>
          )}
        </div>

        {!!selectedVariantPrice?.showPricePresetLabel &&
          !!selectedVariantPrice?.pricePresetName && (
            <AdvancedPricingTag
              presetName={selectedVariantPrice.pricePresetName}
            />
          )}

        {description && <BodyText color="body-2">{description}</BodyText>}
        <ProductAllergyInformation />
      </div>
    </div>
  )
}
